import React, { useEffect, useRef, useState } from "react";
import {
  LIVE_REVIEW_SECTION_MAX_ITEMS,
  LIVE_REVIEW_SECTION_MIN_ITEMS,
} from "../_socket/LiveReviewSocketStore";
// The three fields themselves — shared with `AddWigDialog`, which is the same
// form as a modal for the connections map. See that file for why there are two
// presentations and only one fieldset.
import WigFieldset, {
  canSubmitWig,
  toWigPayload,
  wigPhraseFor,
} from "./WigFieldset";
import "./AddWigRow.scss";

// ═════════════════════════════════════════════════════════════════════════════
// "+ Add a WIG" — the composer for adding a WIG during Live Review.
//
// ⚠️ IDENTICAL IN TWO REPOSITORIES. It imports nothing app-specific for that
// reason: the bounds come from the socket module beside it (which is itself
// byte-identical), not from the CEO app's `_config`, which the coach app does
// not have. Keep it that way — an import of anything outside `_shared/` or
// `_socket/` breaks the parity.
//
// It serves all three Live Review stages, which is why it takes its labels and
// its handler as props rather than reaching for a store: the same row sits at
// the end of a section on the finalize screen, under a band on the connections
// map, and under a group on the Year 1 plan.
//
// ── Why a composer and not a dialog ─────────────────────────────────────────
// Adding a WIG happens mid-conversation, with the other person watching the
// same screen. A modal would cover the very list the new WIG has to make sense
// against — the other three or four in that dimension — and the first question
// anyone asks before adding one is "what have we got already". So the form
// opens IN the list, in the slot the card will occupy, and the list stays
// visible around it.
//
// It is shaped like the card it will become for the same reason: what you are
// filling in is the thing you are about to see.
// ═════════════════════════════════════════════════════════════════════════════

const EMPTY_DRAFT = { label: "", value: "", rationale: "" };

const PlusIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M12 5v14M5 12h14"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * @param sectionKey   Passed straight back to `onAdd`; this component never
 *                     reads it.
 * @param sectionLabel The dimension's name for the button copy ("Financial").
 *                     Optional — the copy falls back to a plain "WIG".
 * @param count        How many WIGs the section holds now, for the cap.
 * @param pending      True while this section's add is in flight.
 * @param onAdd        `(sectionKey, { label, value, rationale }) => void`.
 *                     `label` and `value` are always non-empty when this fires;
 *                     `rationale` may be "". Those are the contract's own
 *                     requirements, not this form's — see `canSubmit`.
 * @param disabled     Locked blueprint: renders nothing at all.
 */
const AddWigRow = ({
  sectionKey,
  sectionLabel = "",
  count = 0,
  pending = false,
  onAdd,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const firstFieldRef = useRef(null);

  // Closes itself when the add lands. `pending` going true-then-false IS the
  // success signal — there is no separate ack, and the new card appearing in
  // the list above is the confirmation the user actually reads.
  const wasPending = useRef(false);
  useEffect(() => {
    if (wasPending.current && !pending) {
      setOpen(false);
      setDraft(EMPTY_DRAFT);
    }
    wasPending.current = pending;
  }, [pending]);

  useEffect(() => {
    if (open && firstFieldRef.current) firstFieldRef.current.focus();
  }, [open]);

  // A locked blueprint gets no affordance at all rather than a disabled one.
  // The row is an invitation, and there is nothing to explain at this point in
  // the flow — the whole screen is read-only and says so elsewhere.
  if (disabled) return null;

  const atCap = count >= LIVE_REVIEW_SECTION_MAX_ITEMS;

  // ⚠️ THE RULE IS THE FIELDSET'S, NOT THIS COMPONENT'S. `canSubmitWig` is the
  // contract's requirement — Name and The goal both non-empty — and lives
  // beside the fields so the dialog on the connections map cannot enforce
  // something different from the row on the other two screens.
  const canSubmit = canSubmitWig(draft) && !pending;

  const patch = (next) => setDraft((prev) => ({ ...prev, ...next }));

  const submit = () => {
    if (!canSubmit || !onAdd) return;
    onAdd(sectionKey, toWigPayload(draft));
  };

  const cancel = () => {
    setOpen(false);
    setDraft(EMPTY_DRAFT);
  };

  // ⚠️ Cmd/Ctrl+Enter, not bare Enter. `value` and `rationale` are textareas
  // holding sentences, and a WIG's wording is exactly the kind of thing someone
  // types a line break into while thinking. Bare Enter submitting would eat
  // that. Escape cancels, which is the pair everyone expects.
  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      cancel();
      return;
    }
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      submit();
    }
  };

  if (atCap) {
    // Stated rather than hidden, and stated as a fact rather than an error.
    // A row that simply disappears at ten reads as a bug; someone who has just
    // filled a dimension and finds nothing there has no way to know whether
    // they hit a limit or the page broke.
    return (
      <p className="awr-cap mb-0">
        {LIVE_REVIEW_SECTION_MAX_ITEMS} WIGs — that&apos;s the most one
        dimension can hold.
      </p>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        className="awr-trigger"
        onClick={() => setOpen(true)}
      >
        <span className="awr-trigger-plus">
          <PlusIcon />
        </span>
        <span className="awr-trigger-text">
          Add {wigPhraseFor(sectionLabel)}
        </span>
      </button>
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="awr-form" onKeyDown={onKeyDown}>
      <WigFieldset
        idPrefix={`awr-${sectionKey}`}
        draft={draft}
        onChange={patch}
        firstFieldRef={firstFieldRef}
      />

      <div className="awr-actions">
        {/* The count sits with the buttons rather than above the form: it is
            information for the moment of committing, not for the moment of
            typing, and at the top it would read as a limit being enforced
            before anything had been done. */}
        <span className="awr-count">
          {count + 1} of {LIVE_REVIEW_SECTION_MAX_ITEMS}
        </span>
        <div className="awr-actions-right">
          <button type="button" className="awr-btn" onClick={cancel}>
            Cancel
          </button>
          <button
            type="button"
            className="awr-btn awr-btn--primary"
            onClick={submit}
            disabled={!canSubmit}
          >
            {pending ? "Adding…" : "Add WIG"}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Can this WIG be removed?
 *
 * Exported beside the row because it is the same rule from the other end, and
 * both halves of "a section holds between one and ten" should be answered in
 * one place. Every delete affordance in Live Review goes through it.
 */
export const canDeleteLiveReviewItem = (count) =>
  count > LIVE_REVIEW_SECTION_MIN_ITEMS;

export default AddWigRow;
